import { HTImage } from "src/components/image";
import { FC, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Column,
  Combobox,
  GroupedCombobox,
  IconButton,
  RefreshIcon,
  Text,
  TraitIcon,
} from "@hightouch/htui";
import { isPresent } from "ts-extras";

import { useDestinationForm } from "src/contexts/destination-form-context";
import { usePermissionContext } from "src/components/permission/permission-context";
import { NEW_ICON_MAP } from "src/utils/destinations";

import { useQuery } from "src/formkit/hooks";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import {
  ExtendedOption,
  FormkitGraphQLReference,
  toExtendedOption,
} from "@hightouch/formkit";
import { ExtendedAssociationOption } from "@hightouch/formkit/src/api/components/associationOption";
import { Mapper } from "./mapper";
import {
  MappingType,
  AdvancedColumn,
  JsonColumnProps,
} from "src/formkit/components/types";
import { ADVANCED_ASSOCIATION_MAPPER_DESTINATION_TYPES } from "./utils";
import { HiddenFormLabel } from "src/components/form/hidden-form-label";

type Lookup = {
  by: string | undefined;
  byType: string | undefined;
  from: string | AdvancedColumn; //supporting advanced columns with nested properties
  object: string;
};

type Value = {
  lookup: Lookup;
  to: string;
  type: "reference";
};

export type AssociationOptions =
  | ExtendedAssociationOption
  | FormkitGraphQLReference;

type Props = {
  name: string;
  // Static association options or handler reference to fetch props base on asc object
  associationOptions?: AssociationOptions;
  associationObjectLabels?: Record<string, unknown>;
  index: number;
  onChange?: (value: Value) => void;
  value: Value;
  isDisabled?: boolean;
};

export const AssociationMapper: FC<Readonly<Props>> = ({
  associationOptions,
  index,
  name,
  onChange,
  value,
  isDisabled = false,
}) => {
  const {
    columns,
    destination,
    destinationDefinition,
    model,
    sourceDefinition,
  } = useFormkitContext();

  // FE feature flags do not have access to the destination type.
  // Do not enable this for destinations that use the associations framework.
  const useAdvancedAssociationMapper =
    ADVANCED_ASSOCIATION_MAPPER_DESTINATION_TYPES.includes(
      destination?.type || "",
    );

  const [jsonColumnProperties, setJsonColumnProperties] =
    useState<JsonColumnProps>({
      selectedColumnProps: undefined,
      allColumnsProps: undefined,
    });

  const { errors } = useDestinationForm();
  const asyncAscOptions =
    associationOptions?.["query"] &&
    typeof associationOptions?.["query"] === "string" &&
    isPresent(associationOptions);

  const variables = {
    ...associationOptions?.variables?.["input"]?.variables,
    ...{ object: value?.lookup?.object ?? value?.to },
  };

  const useAsyncOptions = Boolean(
    asyncAscOptions && (value?.lookup?.object ?? value?.to),
  );

  const {
    data: associatedObjectFields,
    isFetching,
    refetch,
  } = useQuery(
    JSON.stringify({
      name,
      variables: {
        ...associationOptions?.variables,
        input: { ...associationOptions?.variables?.["input"], variables },
      },
    }),
    {
      enabled: useAsyncOptions,
      fetchProps: {
        destinationId: destination?.id,
        modelId: model?.id,
        query: associationOptions?.["query"],
        variables: {
          ...associationOptions?.variables,
          input: { ...associationOptions?.variables?.["input"], variables },
        },
      },
    },
  );

  // Returning an `Option[]` from the handler should be considered deprecated,
  // this component now supports `FormkitAssociationOptionsHandler` which will allow
  // you to return `AssociationOption`.
  const ascFields: ExtendedOption[] = toExtendedOption(
    asyncAscOptions
      ? Array.isArray(associatedObjectFields)
        ? associatedObjectFields
        : associatedObjectFields?.[value.lookup.object]
      : associationOptions?.[value.lookup.object],
  );

  const permission = usePermissionContext();

  // Items in `columns` have `options` field as optional,
  // but `GroupedCombobox` expects it to be required
  const columnOptionGroups = useMemo(() => {
    return (columns ?? []).map((group) => ({
      ...group,
      options: group.options ?? [],
    }));
  }, [columns]);

  const isRealtimeAudience = Boolean(model?.is_realtime_audience);

  return (
    <Box aria-label={`${name} association mapper`}>
      <Column gap={2}>
        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px max-content"
        >
          <Text>Find</Text>
          <Badge>{value?.lookup?.object ?? value?.to}</Badge>
        </Box>

        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px 16px minmax(0, 1fr) 24px"
        >
          <Text>where</Text>

          {destinationDefinition?.icon && (
            <HTImage
              src={destinationDefinition.icon}
              width="4"
              alt={destinationDefinition.name}
            />
          )}

          <HiddenFormLabel label="Select a field">
            <Combobox
              isDisabled={isDisabled || permission.unauthorized}
              isInvalid={Boolean(errors?.[`${name}[${index}].lookup.by`])}
              optionAccessories={(option) => {
                const type = option?.extendedType?.type;

                return type
                  ? {
                      leftAccessory: {
                        type: "icon",
                        icon: NEW_ICON_MAP[type],
                      },
                    }
                  : undefined;
              }}
              optionLabel={(option) => {
                return option.object?.label
                  ? `${option.label} (${option.object.label})`
                  : option.label;
              }}
              options={ascFields ?? []}
              placeholder={value?.lookup?.by ? "" : "Select a field"}
              value={value?.lookup?.by}
              width="100%"
              onChange={(newValue) => {
                const option = ascFields.find(
                  (option) => option.value === newValue,
                );

                onChange?.({
                  to: value.to,
                  type: value.type,
                  lookup: {
                    ...value?.lookup,
                    by: newValue,
                    byType: option?.extendedType?.type,
                  },
                });
              }}
            />
          </HiddenFormLabel>

          {useAsyncOptions && (
            <IconButton
              aria-label="Refresh"
              isDisabled={isDisabled || permission.unauthorized}
              icon={RefreshIcon}
              isLoading={isFetching}
              size="sm"
              variant="secondary"
              onClick={() => {
                void refetch();
              }}
            />
          )}
        </Box>

        <Box
          alignItems="center"
          display="grid"
          gap={2}
          gridTemplateColumns="56px 16px minmax(0, 1fr)"
        >
          <Text>equals</Text>

          {sourceDefinition?.icon && (
            <HTImage
              src={sourceDefinition.icon}
              width="4"
              alt={sourceDefinition.name}
            />
          )}

          {useAdvancedAssociationMapper ? (
            <Mapper
              enableInLineMapper={false}
              isDisabled={isDisabled || permission.unauthorized}
              isError={Boolean(errors?.[`${name}[${index}].lookup.from`])}
              isRealtimeSync={isRealtimeAudience}
              jsonColumnProperties={jsonColumnProperties}
              placeholder={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
              selectedOption={undefined} // not needed because the to side is not going to change our values
              templates={[]}
              value={
                typeof value.lookup.from === "object"
                  ? (value?.lookup?.from ?? undefined)
                  : (value?.lookup ?? undefined)
              }
              onReloadEligibleInlineMapperColumns={() => {}}
              //If the mapping type is a column (standard), use the from property with type string
              //If the mapping type is static, variable, or template, use the from property with type object
              onChange={(newMapping) => {
                if (newMapping.type === MappingType.STANDARD) {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value.lookup,
                      from: newMapping.from,
                    },
                  });
                } else {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value.lookup,
                      from: newMapping,
                    },
                  });
                }
              }}
              onChangeJsonColumnProperties={setJsonColumnProperties}
            />
          ) : (
            <HiddenFormLabel label="Select a column">
              <GroupedCombobox
                isDisabled={isDisabled || permission.unauthorized}
                isInvalid={Boolean(errors?.[`${name}[${index}].lookup.from`])}
                optionAccessories={(option) => {
                  return typeof option?.value === "object" &&
                    option?.value?.type === "additionalColumnReference"
                    ? {
                        leftAccessory: { type: "icon", icon: TraitIcon },
                      }
                    : undefined;
                }}
                optionGroups={columnOptionGroups}
                placeholder={value?.lookup?.from ? "" : "Select a column"}
                value={value?.lookup?.from}
                width="100%"
                onChange={(newValue) => {
                  onChange?.({
                    ...value,
                    lookup: {
                      ...value?.lookup,
                      from: newValue ?? "",
                    },
                  });
                }}
              />
            </HiddenFormLabel>
          )}
        </Box>
      </Column>
    </Box>
  );
};
